import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getConfigs } from "app/reducers/configSlice";
import { getSlotState } from "app/reducers/slotsSlice";
import { fetchConfigs, updateConfigs } from "app/thunks/configAPI";
import { fetchSlots } from "app/thunks/slotsAPI";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation";
import { SESSION_STATUS } from "interfaces/Sessions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import {
  BUTTON_STYLES,
  BUTTON_TYPES,
  Spinner,
  Title,
  useCustomToast,
} from "ui";

import ContainerSlots from "./ContainerSlots";

export interface ConfigData {
  configId: string;
  containerId: string;
  slotId: string | null;
}

export const ConfigScreen = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const {
    configs,
    isFetching: configsFetching,
    isUpdating,
  } = useAppSelector(getConfigs);
  const { slots, isFetching: slotsFetching } = useAppSelector(getSlotState);

  const [selectedValues, setSelectedValues] = useState<
    Record<string, ConfigData[]>
  >({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isUpdateCompleted, setIsUpdateCompleted] = useState(false);
  const initialValuesRef = useRef<any>({});
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const formattedSelectedValues = Object.values(selectedValues).flat();

  const areAllSlotsNull = formattedSelectedValues.every(
    (config) => config.slotId === null,
  );

  const selectOptions = useMemo(() => {
    if (!slots) return [];
    return slots.map(({ number, _id }) => ({
      label: `Casco ${number}`,
      value: _id,
    }));
  }, [slots]);

  const onValueChange = ({
    configId,
    containerId,
    newSlotId,
  }: {
    configId: string;
    containerId: string;
    newSlotId: string;
  }) => {
    const newState = { ...selectedValues };

    newState[configId] = newState[configId].map((item) => {
      if (item.containerId === containerId) {
        return {
          ...item,
          slotId: newSlotId,
        };
      }
      return item;
    });
    setSelectedValues(newState);
  };

  const handleUpdate = async () => {
    try {
      const formattedData = Object.values(selectedValues).flat();
      await dispatch(updateConfigs({ configs: formattedData })).unwrap();

      await dispatch(fetchConfigs()).unwrap();
      await dispatch(fetchSlots("true"));
      setIsUpdateCompleted(true);
      showSuccessToast({
        title: "¡Listo!",
        message: "Ya podemos iniciar una partida",
      });
    } catch (_) {
      showErrorToast({
        title: "Lo sentimos",
        message: "No pudimos vincular los cascos",
      });
    }
  };

  useEffect(() => {
    dispatch(fetchConfigs());
    dispatch(fetchSlots("true"));
  }, [dispatch]);

  useEffect(() => {
    if (configs.length) {
      const newInitialValues = configs.reduce<Record<string, ConfigData[]>>(
        (acc, item) => {
          const configId = item._id;
          const containerSlots = item.containerSlots
            .filter((containerSlot) => containerSlot.containerId !== null)
            .map((containerSlot) => ({
              slotId: containerSlot.slotId?._id || null,
              containerId: containerSlot.containerId._id,
              configId: item._id,
            }));

          return { ...acc, [configId]: containerSlots };
        },
        {},
      );
      initialValuesRef.current = newInitialValues;
      setSelectedValues(newInitialValues);
    }
  }, [configs]);

  useEffect(() => {
    setHasChanges(
      JSON.stringify(initialValuesRef.current) !==
        JSON.stringify(selectedValues),
    );
  }, [selectedValues]);

  const isSessionStarted = useMemo(() => {
    return configs.some((config) =>
      config.sessions.some(
        (session) =>
          session.status === SESSION_STATUS.READY ||
          session.status === SESSION_STATUS.IN_PROGRESS ||
          session.status === SESSION_STATUS.STANDBY,
      ),
    );
  }, [configs]);

  if (configsFetching || slotsFetching) {
    return (
      <div className="flex w-full justify-center items-center bg-faux-dark-gunmetal h-screen">
        <Spinner size={60} />
      </div>
    );
  }

  if (isSessionStarted) {
    return (
      <div className="flex-col w-full min-h-screen px-14 pt-16 bg-faux-dark-gunmetal">
        <Title text="Configuraciones" className="mb-[200px]" />
        <div className="flex flex-col w-full justify-center items-center bg-faux-dark-gunmetal gap-4">
          <WarningAmberIcon
            fontSize="large"
            className="text-yellow-400 w-[2em] h-[2em]"
            aria-hidden="true"
          />
          <p className="text-white text-2xl">
            No se pueden realizar cambios en Configuraciones porque existen
            sesiones activas.
          </p>
          <p className="text-white text-2xl">Finalícelas para continuar.</p>
          <MUIButton
            onClick={() => history.push(NAVIGATOR.sessions)}
            variant="contained"
            className={classNames("w-48", BUTTON_STYLES[BUTTON_TYPES.HOME])}
          >
            Ver sesiones
          </MUIButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-col w-full min-h-screen px-14 pt-8 pb-12 bg-faux-dark-gunmetal">
      <Title text="Configuraciones" />
      <>
        <div className="flex flex-wrap h-2/3 justify-center gap-8">
          {configs.map((config, index) => (
            <ContainerSlots
              key={config._id}
              configIndex={index}
              config={config}
              selectedValues={selectedValues}
              selectOptions={selectOptions}
              onValueChange={onValueChange}
            />
          ))}
        </div>
        <div className="flex justify-end mt-10 pr-64">
          <MUIButton
            variant="contained"
            disabled={!hasChanges || areAllSlotsNull}
            onClick={handleUpdate}
            className={classNames(BUTTON_STYLES[BUTTON_TYPES.CONFIRM])}
            type="submit"
          >
            {isUpdating ? <Spinner color="white" /> : <p>Guardar</p>}
          </MUIButton>
        </div>
        <div className="flex justify-center mt-10">
          <MUIButton
            variant="contained"
            disabled={!isUpdateCompleted}
            onClick={() => history.push(NAVIGATOR.home)}
            className={classNames("w-1/3", BUTTON_STYLES[BUTTON_TYPES.AUTH])}
          >
            Continuar seteo
          </MUIButton>
        </div>
      </>
    </div>
  );
};
